import { useMemo, useCallback } from 'react';
import { useUrlFilters } from './useUrlState';

/**
 * Custom hook for managing entity filters and KPI filters with URL synchronization
 *
 * Entity filters: sessionId, agent, tool, model, workflow, service, user (filter by entity names)
 *
 * All filters are automatically synchronized with URL query parameters.
 *
 * @param {Object} params - Initial filter values (optional)
 * @returns {Object} Filter state and handlers
 */
export default function useFilterManager({
  sessionId,
  agentName,
  toolName,
  modelName,
  workflowName,
  serviceName,
  userId
} = {}) {

  // Define default filter values
  const defaultFilters = {
    sessionId: sessionId || '',
    agent: agentName || '',
    tool: toolName || '',
    model: modelName || '',
    workflow: workflowName || '',
    service: serviceName || '',
    user: userId || '',
    signalGroup: '',  // 'positive', 'negative', 'agentic', 'safety', or 'custom'
    outcome: '',  // execution result (tw.outcome_final): success/failure/cancelled/…
  };

  // Use URL-synced filters (always enabled)
  const [filters, setFilters, updateFilter, clearFilter] = useUrlFilters(defaultFilters);

  // Active filters object for easy enumeration
  const activeFilters = useMemo(() => {
    const active = {};
    if (filters.sessionId) active.sessionId = filters.sessionId;
    if (filters.agent) active.agent = filters.agent;
    if (filters.tool) active.tool = filters.tool;
    if (filters.model) active.model = filters.model;
    if (filters.workflow) active.workflow = filters.workflow;
    if (filters.service) active.service = filters.service;
    if (filters.user) active.user = filters.user;
    if (filters.signalGroup) active.signalGroup = filters.signalGroup;
    if (filters.outcome) active.outcome = filters.outcome;
    return active;
  }, [filters]);

  // Add entity filter
  const addEntityFilter = useCallback((filterKey, value) => {
    if (!filterKey) throw new Error('addEntityFilter requires filterKey');
    if (value === undefined || value === null) throw new Error('addEntityFilter requires value');
    updateFilter(filterKey, value);
  }, [updateFilter]);

  // Remove entity filter
  const removeEntityFilter = useCallback((filterKey) => {
    if (!filterKey) throw new Error('removeEntityFilter requires filterKey');
    clearFilter(filterKey);
  }, [clearFilter]);

  // Toggle KPI filter
  // Clear all filters
  const clearAllFilters = useCallback(() => {
    setFilters(defaultFilters);
  }, [setFilters, defaultFilters]);

  return {
    // Entity filter states
    sessionIdFilter: filters.sessionId,
    agentFilter: filters.agent,
    toolFilter: filters.tool,
    modelFilter: filters.model,
    workflowFilter: filters.workflow,
    serviceFilter: filters.service,
    userFilter: filters.user,
    signalGroupFilter: filters.signalGroup,
    outcomeFilter: filters.outcome,

    // KPI filter states

    // Active filters
    activeFilters,

    // Entity filter handlers
    addEntityFilter,
    removeEntityFilter,

    // KPI filter handlers

    // Utility
    clearAllFilters
  };
}
